import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AlertTriangle, X } from "lucide-react";
import Button from "../ui/Button";
import { USE_MOCK_AUTH } from "../../config/api";
import { escalateOpsCase } from "../../services/institutionWorkspaceApi";
import { showError, showSuccess } from "../../utils/toast";

export default function EscalateCaseModal({ caseRow, onClose, onSuccess }) {
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!caseRow) return undefined;
    setReason("");
    setSaving(false);
    return undefined;
  }, [caseRow]);

  if (!caseRow) return null;

  const handleSubmit = async () => {
    const text = reason.trim();
    if (text.length < 10) {
      showError("Describe why this case should go to your manager (at least 10 characters).");
      return;
    }
    setSaving(true);
    try {
      let result = null;
      if (useLiveApi) {
        result = await escalateOpsCase(token, caseRow.id, { reason: text });
        const manager = result?.escalation?.assigned_to_name || "your manager";
        showSuccess(`${caseRow.id} was sent to ${manager}.`);
      } else {
        showSuccess(`${caseRow.id} was sent to your manager.`);
      }
      onSuccess?.(result);
      onClose?.();
    } catch (error) {
      showError(error.message || "Could not escalate this case.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="escalate-case-title"
        className="flex max-h-[96vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-primary/[0.06] px-5 py-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
              Escalate · {caseRow.id}
            </p>
            <h2 id="escalate-case-title" className="mt-1 text-lg font-bold text-primary">
              Send to your manager
            </h2>
            <p className="mt-1 text-sm text-primary/55">
              {caseRow.customer} goes to the manager who assigned this case. They can send it on to the commercial executive.
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg p-1.5 text-primary/40 hover:bg-primary/5 hover:text-primary"
            aria-label="Close"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4">
          <label className="block text-sm font-semibold text-primary" htmlFor="escalate-reason">
            Reason
          </label>
          <textarea
            id="escalate-reason"
            className="mt-2 min-h-28 w-full rounded-xl border border-primary/15 px-3 py-2 text-sm text-primary outline-none focus:border-primary/40"
            maxLength={255}
            placeholder="What the manager needs to know before they review this case."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2 border-t border-primary/[0.06] px-5 py-4">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={handleSubmit} loading={saving}>
            <AlertTriangle className="h-3.5 w-3.5" />
            Escalate
          </Button>
        </div>
      </div>
    </div>
  );
}
